import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTradingSettings } from "./trading-settings-context";
import type { AccountMode } from "./types";
import { CheckIcon, AlertTriangleIcon } from "lucide-react";
import { usePositions } from "@/solana/hooks/use-positions";
import { AccountsApi } from "@/backend/api-client";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { useAccountStore } from "@/backend/store";
import { toast } from "sonner";

type AccountModeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AccountModeDialog = ({
  open,
  onOpenChange,
}: AccountModeDialogProps) => {
  // accountMode comes from Zustand store (backend) via TradingSettingsProvider
  const { accountMode } = useTradingSettings();
  const [selectedMode, setSelectedMode] = useState<AccountMode>(accountMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { positions } = usePositions();
  const { userId } = useUnifiedAuth();

  // Sync selectedMode with accountMode when dialog opens or accountMode changes
  // This ensures the dialog always reflects the current account mode from the backend
  useEffect(() => {
    // Always sync when dialog opens to ensure correct initial state
    if (open) {
      setSelectedMode(accountMode);
    }
  }, [open, accountMode]);

  // Also sync if accountMode changes while dialog is open (e.g., from WebSocket update)
  useEffect(() => {
    setSelectedMode(accountMode);
  }, [accountMode]);

  const hasOpenPositions = positions.length > 0;
  const isSwitchingMode = selectedMode !== accountMode;
  const canConfirm = !hasOpenPositions || !isSwitchingMode;

  const handleConfirm = async () => {
    if (!canConfirm || !userId) return;
    
    setIsSubmitting(true);
    try {
      // Call backend API to update margin mode
      const response = await AccountsApi.updateMarginMode(selectedMode);
      
      // Sync the updated account to the store
      // This is the single source of truth - the store update will propagate
      // to all UI components via TradingSettingsProvider
      const accountData = await AccountsApi.getAccount();
      useAccountStore.getState().syncFromHttp({ account: accountData });
      
      toast.success(response.message);
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update margin mode";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Account Mode</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-4">
          {/* Warning Alert */}
          {hasOpenPositions && (
            <div className="flex items-start gap-3 px-4 py-3 bg-warning/10 border border-warning/20 rounded-lg">
              <AlertTriangleIcon className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-warning">
                  Cannot Switch Account Mode
                </p>
                <p className="text-xs text-muted-foreground">
                  Please close all open positions before switching between Cross and Isolated margin modes.
                </p>
              </div>
            </div>
          )}

          {/* Isolated Mode */}
          <button
            onClick={() => {
              if (hasOpenPositions && accountMode !== "isolated") return;
              setSelectedMode("isolated");
            }}
            disabled={hasOpenPositions && accountMode !== "isolated"}
            className={`flex flex-col gap-2 p-4 rounded-lg border-2 text-left transition-colors ${
              selectedMode === "isolated"
                ? "border-[#2ebd85] bg-[#2ebd85]/10"
                : accountMode === "isolated"
                ? "border-border bg-background"
                : "border-border hover:border-primary/50"
            } ${
              hasOpenPositions && accountMode !== "isolated"
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Isolated</span>
                {accountMode === "isolated" && selectedMode !== "isolated" && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">Current</span>
                )}
                {selectedMode === "isolated" && accountMode !== "isolated" && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-[#2ebd85]/20 text-[#2ebd85] font-medium">→ Switch to</span>
                )}
              </div>
              {selectedMode === "isolated" && (
                <CheckIcon className="h-5 w-5 flex-shrink-0 text-[#2ebd85]" />
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Each position uses its own dedicated margin. If a position's margin falls below its maintenance threshold, only that position is liquidated. Your other positions and remaining balance are protected.
            </p>
          </button>

          {/* Cross Mode */}
          <button
            onClick={() => {
              if (hasOpenPositions && accountMode !== "cross") return;
              setSelectedMode("cross");
            }}
            disabled={hasOpenPositions && accountMode !== "cross"}
            className={`flex flex-col gap-2 p-4 rounded-lg border-2 text-left transition-colors ${
              selectedMode === "cross"
                ? "border-[#2ebd85] bg-[#2ebd85]/10"
                : accountMode === "cross"
                ? "border-border bg-background"
                : "border-border hover:border-primary/50"
            } ${
              hasOpenPositions && accountMode !== "cross"
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Cross</span>
                {accountMode === "cross" && selectedMode !== "cross" && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">Current</span>
                )}
                {selectedMode === "cross" && accountMode !== "cross" && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-[#2ebd85]/20 text-[#2ebd85] font-medium">→ Switch to</span>
                )}
              </div>
              {selectedMode === "cross" && (
                <CheckIcon className="h-5 w-5 flex-shrink-0 text-[#2ebd85]" />
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              All positions share your total account equity as margin. If your account health falls below the maintenance threshold, positions will be liquidated one by one (starting with the riskiest) until your account is healthy again.
            </p>
          </button>
        </div>

        <div className="flex gap-3">
          <Button 
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="flex-1"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            className="flex-1 bg-[#2ebd85] hover:bg-[#2ebd85]/90 text-white"
            disabled={!canConfirm || isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Confirm"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

